import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';

import {
  dateTimeFacade,
  dateTimeHelper,
  ETimeFormat,
  ETimeNumericFormat,
  getDurationTranslations,
  getTimeOptions,
  LoadingService,
} from '@cf/core';

import { NgForOf, NgIf } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TimeSettingsComponent } from '@cf/shared/components';
import { SwitchChangeEvent } from '@cf/shared/forms/primitives';
import { ButtonComponent, DurationToggleComponent, LinkComponent } from '@cf/shared/ui/button';
import {
  DateFormatPipe,
  TextComponent,
  UnavailableReasonTranslatePipe,
} from '@cf/shared/ui/typography';
import {
  ETimezoneSettings,
  IEvent,
  IEventContainer,
  IFreeSlot,
  ITimeSlot,
  ITimeslots,
  TIME_CONFIG,
  TimeFormatEnum,
} from '@cf/temp/services';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
@Component({
  selector: 'cf-booking-picker',
  templateUrl: './booking-picker.component.html',
  styleUrls: ['./booking-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TextComponent,
    DateFormatPipe,
    RouterLink,
    NgxTolgeeModule,
    NgIf,
    ButtonComponent,
    LinkComponent,
    DurationToggleComponent,
    TimeSettingsComponent,
    NgForOf,
    NgbPopover,
    UnavailableReasonTranslatePipe,
  ],
  standalone: true,
})
export class BookingPickerComponent implements OnChanges {
  translateService = inject(TranslateService);
  @ViewChild(DurationToggleComponent) toggleComponent!: DurationToggleComponent;

  @ViewChildren('p') reasonPops!: QueryList<NgbPopover>;

  @Input() timeslot!: ITimeslots;

  @Input() timeslots!: ITimeslots[];

  @Input() formerSlot!: IFreeSlot;

  @Input() durations!: string[];

  @Input({ required: true }) duration!: string;

  @Input() events!: IEventContainer[];

  @Input({ required: true }) isAuthorized!: boolean | null;

  @Input({ required: true }) isTouch!: boolean | null;

  @Input({ required: true }) timezone!: string;

  @Input() disableTimezone!: boolean;

  @Input() hasFreeSlots!: boolean;

  @Input() troubleshoot!: boolean;

  @Input() isPreview!: boolean;

  @Input() timezoneSetting!: ETimezoneSettings;

  @Input() hourFormat!: string;

  @Output() emitTimezone = new EventEmitter();

  @Output() emitDuration = new EventEmitter();

  @Output() emitConfirm = new EventEmitter();

  @Output() emitFocus = new EventEmitter();

  @Output() emitNextSlot = new EventEmitter();

  selectedSlot!: string;

  timeSlots: ITimeSlot[] = [];

  _timeFormat: ETimeNumericFormat = TIME_CONFIG.timeFormat;

  private loading!: boolean;

  constructor(
    public readonly cd: ChangeDetectorRef,
    private readonly loadingService: LoadingService,
  ) {
    this.loadingService.loadingState$.subscribe((value) => {
      this.loading = value;
    });
  }

  get timeFormat() {
    return this._timeFormat === 12 ? ETimeFormat.TIME_12 : ETimeFormat.TIME_24;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hourFormat) {
      this._timeFormat =
        this.hourFormat === TimeFormatEnum['24h']
          ? ETimeNumericFormat.TIME_24
          : ETimeNumericFormat.TIME_12;
    }
    if (changes.timeslot) {
      this.getTimeOptions();
      this.selectedSlot = '';
    }
  }

  onScroll() {
    if (this.troubleshoot) {
      this.reasonPops.forEach((pop) => {
        pop.close();
      });
    }
  }

  onDurationChange(duration: number) {
    this.getTimeOptions();
    this.emitDuration.emit(`${duration}`);
    this.cd.detectChanges();
    this.cd.markForCheck();
  }

  formatChange(toggleChange: SwitchChangeEvent) {
    if (toggleChange.checked) {
      this._timeFormat = ETimeNumericFormat.TIME_24;
    } else {
      this._timeFormat = ETimeNumericFormat.TIME_12;
    }
    this.getTimeOptions();
  }

  isFormer(time: string, duration: number): boolean {
    if (!this.formerSlot) {
      return false;
    }

    if (duration !== +this.duration) {
      return false;
    }

    return (
      dateTimeHelper.getDateFromISO(this.formerSlot.start).toString() ===
      dateTimeHelper.getDateFromISO(time).toString()
    );
  }

  checkBusyState(time: string) {
    if (this.loading) {
      return false;
    }
    const events = this.events.find(Boolean)?.events;
    return events?.some((e) => {
      return (
        dateTimeHelper.getDateFromISO(time, true).toString() >=
          dateTimeHelper.getDateFromISO(e.start, true).toString() &&
        dateTimeHelper.getDateFromISO(time, true).toString() <
          dateTimeHelper.getDateFromISO(e.end, true).toString()
      );
    });
  }

  focusTime(time: string) {
    this.selectedSlot = time;
    const events = this.events.find(Boolean)?.events;
    events?.forEach((e) => {
      e.highlighted =
        dateTimeHelper.getDateFromISO(time, true).toString() >=
          dateTimeHelper.getDateFromISO(e.start, true).toString() &&
        dateTimeHelper.getDateFromISO(time, true).toString() <
          dateTimeHelper.getDateFromISO(e.end, true).toString();
    });
    this.emitFocus.emit(this.events);
  }

  highlightedEvents(time: string): IEvent[] | undefined {
    if (this.selectedSlot === time) {
      const events = this.events.find(Boolean)?.events;
      return events?.filter(
        (e) =>
          dateTimeHelper.getDateFromISO(time, true).toString() >=
            dateTimeHelper.getDateFromISO(e.start, true).toString() &&
          dateTimeHelper.getDateFromISO(time, true).toString() <
            dateTimeHelper.getDateFromISO(e.end, true).toString() &&
          e.highlighted,
      );
    }

    return undefined;
  }

  private getTimeOptions(): void {
    if (this.timeslot.free_slots?.length) {
      this.timeSlots = getTimeOptions({
        startFrom: this.getNumericHours(this.timeslot.free_slots.find(Boolean)?.start),
        endTime: 24,
        interval: 'base',
        timeFormat: this._timeFormat,
        daysAmount: 0,
        durationTranslations: getDurationTranslations('md', this.translateService),
      }).map((option) => {
        return {
          start: dateTimeFacade.mergeDateTime(this.timeslot.date, option.value).toString(),
          is_available: true,
        };
      });
      // this.timezoneSetting = ETimezoneSettings.AUTO_DETECT;
      // this.disableTimezone = true;
      // debugger;
    } else {
      this.timeSlots = this.timeslot.time_slots ?? [];
      if (!this.isPreview) {
        this.timeSlots = this.timeSlots.map((t) => ({
          ...t,
          is_available: true,
        }));
      }
    }
  }

  private getNumericHours(time?: string) {
    if (!time) {
      return;
    }

    return dateTimeFacade.dateToDuration(time);
  }
}
